/**
 * What an employee document is, apart from the category it is filed under.
 * Mirrors the backend list in `staff-document-type.util.ts`; types that are
 * useless once lapsed must carry an expiry date.
 */
export const STAFF_DOCUMENT_TYPES = [
  { value: 'passport', label: 'Passport', requiresExpiry: true },
  { value: 'visa', label: 'Visa', requiresExpiry: true },
  { value: 'work_permit', label: 'Work permit', requiresExpiry: true },
  { value: 'national_id', label: 'National ID', requiresExpiry: false },
  { value: 'driving_licence', label: 'Driving licence', requiresExpiry: true },
  { value: 'certificate', label: 'Certificate', requiresExpiry: false },
  { value: 'insurance', label: 'Insurance', requiresExpiry: true },
] as const

export type StaffDocumentType = (typeof STAFF_DOCUMENT_TYPES)[number]['value']

const BY_VALUE = new Map<string, (typeof STAFF_DOCUMENT_TYPES)[number]>(
  STAFF_DOCUMENT_TYPES.map((t) => [t.value, t])
)

export const NO_DOCUMENT_TYPE = ''

export const DOCUMENT_TYPE_OPTIONS = [
  { value: NO_DOCUMENT_TYPE, label: 'Other / not set' },
  ...STAFF_DOCUMENT_TYPES.map((t) => ({
    value: t.value,
    label: t.label,
    description: t.requiresExpiry ? 'Needs an expiry date' : undefined,
  })),
]

/** Label for a stored type; candidate files keep their free-text type as is. */
export function documentTypeLabel(value: string | null | undefined): string | null {
  if (!value) return null
  return BY_VALUE.get(value)?.label ?? value
}

export function documentTypeNeedsExpiry(value: string | null | undefined): boolean {
  return !!value && !!BY_VALUE.get(value)?.requiresExpiry
}

/** The message shown when a lapsing type has no expiry date, or null when fine. */
export function missingExpiryMessage(
  type: string | null | undefined,
  expiryDate: string | null | undefined
): string | null {
  if (!documentTypeNeedsExpiry(type) || expiryDate) return null
  return `${documentTypeLabel(type)} documents need an expiry date, so the holder is reminded before they lapse.`
}
